import {useCallback, useMemo, useRef, useState} from "react";
import {planCodeForId, type PaidPlanId} from "pricing/planCatalogue";
import {offerWindowAdmits} from "upsell/offerShowWindow";
import {UpsellApi} from "upsell/UpsellApi";
import type {Plan} from "../gating";
import {upsellCard, type UpsellCardModel, type UpsellTrigger} from "./upsellCard";
import {
	readUpsellCardShown, rememberUpsellCardShown, upsellCardArming, upsellCardDue, upsellSenderKey,
} from "./upsellCardShown";

export interface UpsellCardView {
	/** What the card says, or null while there is no wall to sell past. */
	model: UpsellCardModel | null;
	/**
	 * A wall the composer just met. The quiet window can refuse the FIRST of a visit; once a card is up,
	 * every later wall replaces what it says — unless it sells this sender nothing, which changes nothing.
	 *
	 * Fire-and-forget from the caller's side, although the window it consults is now AWAITED: the
	 * composer's control has nothing to wait for, and the card appears when the answer arrives.
	 */
	arm: (wall: UpsellTrigger) => void;
	/** Called when the card actually comes OUT — a tucked chip shows no offer, so it is not a showing. */
	rememberShown: () => void;
}

/**
 * Thin adapter for the upsell card (kitecorp/transferify-ui#2278, #2514, #2555): it holds the wall the
 * composer last met and asks the React-free rules what that sells. Every rule it consults lives elsewhere
 * — which tier unlocks what, and the figures, in `./upsellCard`; who decides the show window, in
 * `upsell/offerShowWindow`; the browser's own seven-day record in `./upsellCardShown` — so this hook owns
 * state and nothing else (epic #26).
 *
 * <b>The window is the ACCOUNT'S, and the api owns it</b> (kitecorp/transferify-api-service#1774, wired
 * here by ui#2555). `POST /offers/UPSELL_CARD/claim` decides and records the presentation in one
 * transaction, so two tabs — or two browsers — arming a wall in the same second compete for one
 * presentation instead of both being told yes, which is the case the per-browser marker could never
 * answer. The marker stays as the FALLBACK for a request nobody answered, and as the whole rule for a
 * guest, who has no account for a window to belong to.
 *
 * <b>The window is consulted when the wall is ARMED, not on every render.</b> A card that passed the
 * check keeps its place until the composer re-arms it, so the reader never watches it vanish mid-sentence
 * because the window closed under them, and nothing here reads the clock while rendering.
 *
 * <b>And it is consulted for the FIRST card of a visit only</b> (owner 2026-09-17). The record is written
 * the moment a card opens, so consulting the window on every wall left the card naming the control
 * BEFORE the one the sender had just clicked — see `upsellCardArms`.
 *
 * @param plan the sender's own tier — what decides which controls are locked for them
 * @param accountId the signed-in sender's account, or null for a guest
 * @param signedIn whether this reader HAS an account. Separate from `accountId` deliberately: an
 *   anonymous Firebase session carries an id the browser marker is happily keyed by, but no account for
 *   the api's window to belong to — asking on its behalf would only collect 401s.
 */
export function useUpsellCard(plan: Plan, accountId: string | null, signedIn: boolean): UpsellCardView {
	const [wall, setWall] = useState<UpsellTrigger | null>(null);
	const sender = upsellSenderKey(accountId);
	/**
	 * Whether a card this visit armed is on screen — what lifts the quiet window for every later wall.
	 *
	 * A ref rather than state, because nothing renders from it and `arm` must read what the LAST arming
	 * left: a state value closed over by the callback would be one wall behind.
	 */
	const showing = useRef(false);
	/**
	 * This visit's one admission, held as the PROMISE rather than as its answer.
	 *
	 * Two walls can be armed before the first claim comes back — the composer's controls are one click
	 * apart — and a second claim would meet the window the first one just spent and be refused, hiding a
	 * card that was already on its way. So later walls wait on the SAME answer. A refusal is cached for
	 * the same reason in reverse: the window is shut for days, and asking again would spend a request to
	 * be told so.
	 */
	const admission = useRef<Promise<boolean> | null>(null);

	const arm = useCallback((next: UpsellTrigger) => {
		// ASKED FIRST, and the order matters now that the window has a side effect: a wall that sells THIS
		// sender nothing must not spend the account's one presentation. It also leaves whatever is already
		// up alone rather than blanking it. Free senders meet one: choosing their own expiry ceiling arms
		// `maxExpiry`, which is the paying tiers' row.
		const model = upsellCard(plan, next);
		if (model === null) {
			return;
		}
		const arming = upsellCardArming(
			showing.current, signedIn, readUpsellCardShown(sender), Date.now());
		if (arming === "refuse") {
			return;
		}
		if (arming === "arm") {
			showing.current = true;
			setWall(next);
			return;
		}
		admission.current ??= claimUpsellCard(sender, model.target);
		void admission.current.then(admitted => {
			if (!admitted) {
				return;
			}
			showing.current = true;
			setWall(next);
		});
	}, [plan, sender, signedIn]);

	const rememberShown = useCallback(() => {
		rememberUpsellCardShown(sender, Date.now());
	}, [sender]);

	return {model: useMemo(() => upsellCard(plan, wall), [plan, wall]), arm, rememberShown};
}

/**
 * The signed-in sender's own window: the api's answer, or this browser's record where nobody answered.
 *
 * The record is re-read AFTER the claim rather than passed in, because the request takes time and the
 * fallback has to describe the browser as it is when the answer fails to arrive.
 *
 * The tier travels even though this window is account-wide across every wall — the api requires one on
 * every claim, and the honest value is the tier this card would actually sell.
 */
async function claimUpsellCard(sender: string, target: PaidPlanId): Promise<boolean> {
	const claim = await UpsellApi.claimPresentation("UPSELL_CARD", planCodeForId(target));
	return offerWindowAdmits(claim, upsellCardDue(readUpsellCardShown(sender), Date.now()));
}
